import * as React from 'react'
import { Plus, Trash2, CalendarPlus, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { requestErrorMessage } from '@/helpers/apiClient'
import { fmt12, toIsoTime, toTimeInput } from '@/lib/eventTime'
import {
  useEventTimeline,
  useAddTimelineEntry,
  useUpdateTimelineEntry,
  useRemoveTimelineEntry,
  useApplyTimelineTemplate,
} from '@/hooks/useTimeline'
import { useVenueTimelineTemplates } from '@/hooks/useTimeline'
import { useAuth } from '@/auth/AuthContext'
import { can } from '@/auth/access'
import { calendarDay, type CalendarDay, type WallClock } from '@lib/validators/temporal.validator'
import { timeOfDay, toTimeOfDay, type TimeOfDay } from '@lib/validators/time-of-day.validator'
import { spaceHueClass, spaceHues, type SpaceHue } from '@/lib/spaceHue'
import { timelineDay } from '@lib/helpers/timeline-day'
import type { EventDetailDto } from '@lib/dto/event.dto'
import type { EventTimelineEntryDto } from '@lib/dto/event-timeline.dto'
import { EventRunSheet } from '@/components/EventRunSheet'
import { TimelineLabelField, momentName, timelineLabelError } from '@/components/TimelineLabelField'
import { cn } from '@/lib/utils'

/**
 * The day an entry belongs to, as `YYYY-MM-DD`, read off the stored wall clock.
 *
 * Not the event's date: a send-off at 00:30 carries the *following* day. Read to
 * mark a row as after midnight; a typed time is placed by `timelineDay`, so
 * re-timing a row can also take it back to the event's own day.
 */
function dayOf(entry: EventTimelineEntryDto): CalendarDay {
  return calendarDay(entry.startsAt.slice(0, 10))
}

/** Whether this entry falls after midnight, relative to the event's own date. */
function isAfterMidnight(
  entry: EventTimelineEntryDto,
  eventDate: CalendarDay | null,
): boolean {
  return eventDate !== null && dayOf(entry) !== eventDate
}

/**
 * The phases of the day, in clock order, for reading beside the timeline.
 *
 * Phases are shown and never edited here: they are a space's configuration, set
 * in the Phases section and the plan editor. What this view is for is seeing
 * them *against* the run of the day — a grand entrance at 18:10 with no phase
 * holding the space until 18:30 shows as a gap, which is more use than a rule
 * that tried to decide whether the gap was a mistake.
 */
function phasesInOrder(event: EventDetailDto): RunPhase[] {
  const hues = spaceHues(event.venue.spaces)
  // `flatMap` rather than `filter`, because a filter does not narrow the type:
  // the old signature said `startsAt: string | null` and then every caller had
  // to re-check a thing this function had already decided.
  return [...event.phases]
    .flatMap((phase) => {
      const startsAt = phase.startsAt
      return startsAt === null
        ? []
        : [
            {
              id: phase.id,
              label: phase.label,
              space: phase.space.name,
              startsAt,
              hue: hues.get(phase.venueSpaceId),
              transition: phase.type === 'transition',
            },
          ]
    })
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
}

/** A phase as the run sheet marks it: its space's hue, or a flip's diamond. */
interface RunPhase {
  id: string
  label: string
  space: string
  startsAt: WallClock
  hue: SpaceHue | undefined
  transition: boolean
}

/**
 * The two lanes interleaved into one chronological list.
 *
 * A proportional axis across 11:00 to 01:00 squeezes an afternoon of entries
 * into a sliver, and what a coordinator carries is a list. So the phases are
 * folded in as rows at the minute they begin, which reads down the page and
 * prints, rather than drawn as blocks beside it.
 */
function interleave(
  entries: EventTimelineEntryDto[],
  phases: RunPhase[],
): (
  | { kind: 'entry'; at: WallClock; entry: EventTimelineEntryDto }
  | {
      kind: 'phase'
      at: WallClock
      label: string
      space: string
      id: string
      hue: SpaceHue | undefined
      transition: boolean
    }
)[] {
  const rows = [
    ...entries.map((entry) => ({
      kind: 'entry' as const,
      at: entry.startsAt,
      entry,
    })),
    ...phases.flatMap((phase) =>
      phase.startsAt === null
        ? []
        : [
            {
              kind: 'phase' as const,
              at: phase.startsAt,
              label: phase.label,
              space: phase.space,
              id: phase.id,
              hue: phase.hue,
              transition: phase.transition,
            },
          ],
    ),
  ]
  // A phase opening at the same minute as a moment sorts first: the space is
  // ready, and then the thing happens in it.
  // Two moments at the same minute fall through to `sortOrder`, the tiebreak
  // the server already applies. A constant there would answer the same for
  // (a, b) and (b, a) — a comparator that disagrees with itself, leaving the
  // order to the engine, so screen and paper could disagree about the one thing
  // the shared component exists to keep identical.
  return rows.sort((a, b) => {
    const byClock = a.at.localeCompare(b.at)
    if (byClock !== 0) return byClock
    if (a.kind !== b.kind) return a.kind === 'phase' ? -1 : 1
    return a.kind === 'entry' && b.kind === 'entry' ? a.entry.sortOrder - b.entry.sortOrder : 0
  })
}

/**
 * The description of one moment, edited in place.
 *
 * Its own state, so a save the server refuses does not put back the previous
 * words: what was typed stays until it is shortened or saved. The cap is the
 * schema's, checked here so the request is never sent for a description the
 * API would only refuse.
 */
function MomentLabel({
  entry,
  onEdit,
  onCommit,
}: {
  entry: EventTimelineEntryDto
  /** A keystroke, so a previous failure under the list does not linger. */
  onEdit: () => void
  onCommit: (label: string) => void
}) {
  const [value, setValue] = React.useState(entry.label)

  function commit(): void {
    if (timelineLabelError(value) !== null) return
    const trimmed = value.trim()
    if (trimmed === '' || trimmed === entry.label) {
      setValue(entry.label)
      return
    }
    setValue(trimmed)
    onCommit(trimmed)
  }

  return (
    <TimelineLabelField
      value={value}
      onChange={(next) => {
        setValue(next)
        onEdit()
      }}
      onBlur={commit}
      size="sm"
      aria-label={`Description for ${momentName(entry.label)}`}
    />
  )
}

/**
 * The time of one moment, edited in place.
 *
 * Unlike the description, a refused time is put back: the field shows the time
 * the moment is stored at, so one the server did not take does not sit there
 * looking saved. A save that lands, here or from someone else, moves the field
 * with it.
 */
function MomentTime({
  entry,
  onCommit,
}: {
  entry: EventTimelineEntryDto
  /** Save a typed time; `onRefused` puts the stored one back. */
  onCommit: (raw: string, onRefused: () => void) => void
}) {
  const stored = toTimeInput(entry.startsAt)
  // A string, not a time: the field holds whatever is half-typed into it.
  const [value, setValue] = React.useState<string>(stored)
  const [shownFor, setShownFor] = React.useState(stored)
  if (shownFor !== stored) {
    setShownFor(stored)
    setValue(stored)
  }

  return (
    <Input
      type="time"
      size="sm"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onCommit(value, () => setValue(stored))}
      className="font-mono tabular-nums"
      aria-label={`Time for ${momentName(entry.label)}`}
    />
  )
}

/**
 * An event's run of the day.
 *
 * Hosted twice — in the admin's `Timeline` section and in the client's own view
 * of the event — because both may edit it and two implementations of the
 * most-read thing in the app would be two run sheets free to disagree. See
 * `docs/event-timeline.md`.
 */
export function EventTimelineBody({
  event,
  canEdit,
}: {
  event: EventDetailDto
  canEdit: boolean
}) {
  const { user } = useAuth()
  /**
   * Applying a template replaces the whole day, and reads the venue's library to
   * offer the choice — both of which belong to whoever runs the venue: staff who
   * edit events and can see its templates. A client edits their own run of the
   * day row by row, which is what they were given.
   */
  const canApplyTemplate = canEdit && can(user, 'events:edit') && can(user, 'venueSetup:view')
  const timelineQuery = useEventTimeline(event.id)
  const templatesQuery = useVenueTimelineTemplates(
    event.venue.id,
    canApplyTemplate,
  )
  const addEntry = useAddTimelineEntry(event.id)
  const updateEntry = useUpdateTimelineEntry(event.id)
  const removeEntry = useRemoveTimelineEntry(event.id)
  const applyTemplate = useApplyTimelineTemplate(event.id)

  const [error, setError] = React.useState<string | null>(null)
  // The template waiting on a yes: applying one replaces every moment already on the day.
  const [replacingWith, setReplacingWith] = React.useState<{ id: string; name: string } | null>(null)
  const [replaceError, setReplaceError] = React.useState<string | null>(null)
  // Set as the request is sent, not when the mutation's state next renders: a
  // second Enter in between would otherwise add the moment twice.
  const adding = React.useRef(false)
  const [newLabel, setNewLabel] = React.useState('')
  const [newTime, setNewTime] = React.useState<TimeOfDay>(() => timeOfDay('12:00'))

  const entries = timelineQuery.data?.entries ?? []
  // Phases folded in as context rows, on the clock they share.
  const rows = interleave(entries, phasesInOrder(event))
  const templates = templatesQuery.data?.templates ?? []
  const dated = event.date !== null

  /**
   * The day a typed time lands on: the event's own, or the following one in the
   * small hours of an event that runs past midnight. See `timelineDay`.
   */
  function dayFor(hhmm: TimeOfDay, eventDay: CalendarDay): CalendarDay {
    return timelineDay(hhmm, { date: eventDay, startsAt: event.startsAt, endsAt: event.endsAt })
  }

  /**
   * Re-time a row, placing it by the same rule as a new one. It used to stay on
   * the day it sat on, so a row pushed onto the wrong day could not be brought
   * back by correcting its time.
   */
  function retime(entry: EventTimelineEntryDto, raw: string, onRefused: () => void): void {
    const hhmm = toTimeOfDay(raw)
    const startsAt = hhmm === null ? null : toIsoTime(event.date === null ? dayOf(entry) : dayFor(hhmm, event.date), hhmm)
    // A cleared or half-typed time is not saved, and is not left in the field either.
    if (startsAt === null) {
      onRefused()
      return
    }
    setError(null)
    updateEntry.mutate(
      { entryId: entry.id, input: { startsAt } },
      {
        onError: (e) => {
          setError(requestErrorMessage(e))
          onRefused()
        },
      },
    )
  }

  function relabel(entry: EventTimelineEntryDto, label: string): void {
    setError(null)
    updateEntry.mutate(
      { entryId: entry.id, input: { label } },
      { onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  function add(): void {
    if (adding.current) return
    // Checked before the request. The API's refusal is the same rule, and
    // reaching it is how a too-long description became a Sentry issue instead
    // of a sentence under the field. The box keeps what was typed either way.
    if (timelineLabelError(newLabel) !== null) return
    const label = newLabel.trim()
    const eventDay = event.date
    // Narrowed into a binding so the day reaching `dayFor` is a
    // `CalendarDay` and not `CalendarDay | null`. It was the latter, and the
    // template literal would have written the string "null" into a timestamp
    // rather than failing — unreachable behind this guard, but only by luck.
    if (label === '' || eventDay === null) return
    const startsAt = toIsoTime(dayFor(newTime, eventDay), newTime)
    if (startsAt === null) return
    setError(null)
    adding.current = true
    addEntry.mutate(
      { label, startsAt },
      {
        onSuccess: () => {
          setNewLabel('')
        },
        onError: (e) => setError(requestErrorMessage(e)),
        onSettled: () => {
          adding.current = false
        },
      },
    )
  }

  function applyNow(templateId: string): void {
    setError(null)
    applyTemplate.mutate({ templateId }, { onError: (err) => setError(requestErrorMessage(err)) })
  }

  function confirmReplace(): void {
    if (replacingWith === null) return
    applyTemplate.mutate(
      { templateId: replacingWith.id },
      {
        onSuccess: () => {
          setReplacingWith(null)
          setReplaceError(null)
          setError(null)
        },
        onError: (err) => setReplaceError(requestErrorMessage(err)),
      },
    )
  }

  /** One moment of the day: editable for whoever may, plain text otherwise. */
  function renderEntry(entry: EventTimelineEntryDto) {
    return (
      <li key={entry.id} className="ds-run__row">
        {canEdit ? (
          <MomentTime entry={entry} onCommit={(raw, onRefused) => retime(entry, raw, onRefused)} />
        ) : (
          <span className="ds-run__time">{fmt12(entry.startsAt)}</span>
        )}
        <span aria-hidden className="ds-run__mark" />
        <div className="flex min-w-0 items-start gap-2">
          {canEdit ? (
            <MomentLabel entry={entry} onEdit={() => setError(null)} onCommit={(label) => relabel(entry, label)} />
          ) : (
            <p className="ds-run__title min-w-0 whitespace-pre-line break-words">{entry.label}</p>
          )}
          {isAfterMidnight(entry, event.date) ? (
            <Tooltip content="The small hours of the following day">
              <span className="shrink-0 text-xs text-ink-muted">next day</span>
            </Tooltip>
          ) : null}
        </div>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove ${momentName(entry.label)}`}
            disabled={removeEntry.isPending}
            onClick={() =>
              removeEntry.mutate(
                { entryId: entry.id },
                { onError: (e) => setError(requestErrorMessage(e)) },
              )
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : (
          <span />
        )}
      </li>
    )
  }

  if (timelineQuery.isPending)
    return <LoadingState label="Loading the timeline…" />
  if (timelineQuery.isError) {
    return (
      <ErrorState
        message={requestErrorMessage(timelineQuery.error)}
        onRetry={() => void timelineQuery.refetch()}
      />
    )
  }

  if (!dated) {
    return (
      <p className="text-sm text-ink-muted">
        Give this event a date and its day will be laid out from the
        venue&rsquo;s default timeline.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <div className="flex flex-wrap items-center gap-2">
          {canApplyTemplate && templates.length > 0 ? (
            <>
              <span className="text-sm text-ink-muted">
                Start from a template:
              </span>
              <Select
                size="sm"
                value=""
                aria-label="Apply a timeline template"
                className="w-auto"
                onChange={(e) => {
                  const template = templates.find((t) => t.id === e.target.value)
                  if (template === undefined) return
                  // Replacing, not merging — two timelines concatenated describe no
                  // day at all. Ask before anything is lost.
                  if (entries.length === 0) applyNow(template.id)
                  else setReplacingWith({ id: template.id, name: template.name })
                }}
              >
                <option value="">Choose…</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                    {template.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </Select>
            </>
          ) : null}
        </div>
        {entries.length > 0 ? (
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4" /> Print run sheet
          </Button>
        ) : null}
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-ink-muted">
          Nothing on the day yet.{' '}
          {canEdit
            ? 'Add the first moment below, or start from a template.'
            : 'The venue has not laid it out yet.'}
        </p>
      ) : (
        <ul className={cn('ds-run rounded-lg border border-line bg-surface px-4 py-1.5', canEdit && 'ds-run--editing')}>
          {rows.map((row) =>
            row.kind === 'phase' ? (
              /* A space opening, shown for context and never edited here. Muted
                 so the run of the day stays the thing being read; marked in the
                 space's hue, or as a honey diamond when it is a flip. */
              <li
                key={`phase-${row.id}`}
                className={cn(
                  'ds-run__row ds-run__row--set',
                  row.transition ? 'ds-run__row--flip' : row.hue !== undefined && spaceHueClass(row.hue),
                )}
              >
                <span className="ds-run__time">{fmt12(row.at)}</span>
                <span aria-hidden className="ds-run__mark" />
                <p className="ds-run__title min-w-0 truncate">
                  {/* A phase is named after its space by default, and "Barn set
                      for Barn" reads like a bug. Only say the label when it adds
                      something. */}
                  {row.label === row.space ? `${row.space} set` : `${row.space} set for ${row.label}`}
                </p>
                <span />
              </li>
            ) : (
              renderEntry(row.entry)
            ),
          )}
        </ul>
      )}

      {canEdit ? (
        <div className="flex flex-wrap items-start gap-2">
          <Input
            type="time"
            value={newTime}
            onChange={(e) => {
              const parsed = toTimeOfDay(e.target.value)
              if (parsed !== null) setNewTime(parsed)
            }}
            className="w-28"
            aria-label="Time for the new moment"
          />
          <TimelineLabelField
            value={newLabel}
            onChange={(next) => {
              setNewLabel(next)
              setError(null)
            }}
            onKeyDown={(e) => {
              // Enter adds the moment, as it always has; Shift+Enter starts a new
              // line of it. Not while an input method is still composing a word.
              if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
              e.preventDefault()
              add()
            }}
            placeholder="e.g. Grand entrance"
            aria-label="What happens"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={add}
            disabled={newLabel.trim() === '' || timelineLabelError(newLabel) !== null || addEntry.isPending}
          >
            <Plus className="h-4 w-4" /> Add
          </Button>
          <p className="m-0 basis-full text-xs text-ink-muted">Enter adds it. Shift+Enter starts a new line.</p>
        </div>
      ) : null}

      {canApplyTemplate && entries.length === 0 && templates.length === 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-ink-muted">
          <CalendarPlus className="h-3.5 w-3.5" />
          This venue has no timeline templates yet — set one up in its settings
          and new events start from it.
        </p>
      ) : null}

      {error ? <FormError>{error}</FormError> : null}

      <ConfirmDialog
        open={replacingWith !== null}
        onOpenChange={(next) => {
          if (next || applyTemplate.isPending) return
          setReplacingWith(null)
          setReplaceError(null)
        }}
        title={replacingWith ? `Start from “${replacingWith.name}”?` : 'Start from a template?'}
        description={`The ${entries.length === 1 ? 'moment' : `${String(entries.length)} moments`} on this day ${entries.length === 1 ? 'is' : 'are'} replaced by the template’s. Nothing is merged.`}
        confirmLabel="Replace the day"
        destructive
        busy={applyTemplate.isPending}
        error={replaceError}
        onConfirm={confirmReplace}
      />

      {/* Hidden on screen; `@media print` in index.css hides everything else and
          shows this. Built from the same interleaved rows as the list above, so
          what is on paper cannot drift from what is on the page. */}
      <EventRunSheet event={event} entries={entries} />
    </div>
  )
}
